import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePaymentService } from '../services/paymentService';
import globalNotification from '../common/hooks/globalNotification';
import { notificationType } from '../common/constants/notificationType';
import { useCreditsService } from '../services/creditsService';
import { useSearchParams } from 'react-router-dom';
import getErrorTranslations from '../utils/paymentContextHelper';
import { isImpersonationTab } from '../services/impersonationSession';

const PaymentContext = createContext();

export const PaymentProvider = ({ children }) => {
    const { createCheckoutSession } = usePaymentService();
    const { getStripeCreditsPrices } = useCreditsService();
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedPlanFromParams = searchParams.get('plan');
    const lang = localStorage.getItem('i18nextLng');

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const fetchedPlans = await getStripeCreditsPrices();
                setPlans(fetchedPlans);

                const defaultPlan = fetchedPlans.find(plan => plan.isPopular);
                setSelectedPlan(defaultPlan);
            } catch {
                globalNotification.open({
                    type: notificationType.ERROR,
                    message: getErrorTranslations(lang, 'plansFetchError', 'message'),
                    description: getErrorTranslations(lang, 'plansFetchError', 'description'),
                    placement: 'top',
                });
            }
        };

        fetchPlans();
    }, []);

    useEffect(() => {
        if (selectedPlanFromParams) {
            const plan = plans.find(p => p.stripeProductId === selectedPlanFromParams);

            if (plan) {
                setSelectedPlan(plan);

                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('plan');
                setSearchParams(newSearchParams);
            }
        }
    }, [selectedPlanFromParams, plans]);

    useEffect(() => {
        if (!selectedPlanFromParams) return;

        const matchingPlan = plans.find(p => p.stripeProductId === selectedPlanFromParams);

        if (!matchingPlan) return;

        setSelectedPlan(matchingPlan);

        const updatedSearchParams = new URLSearchParams(searchParams);
        updatedSearchParams.delete('plan');
        setSearchParams(updatedSearchParams);
    }, [selectedPlanFromParams, plans, searchParams]);

    const initiatePayment = async vinCode => {
        // a read-only impersonation never starts a checkout for the user
        if (!selectedPlan || isImpersonationTab()) {
            return;
        }

        try {
            const { sessionUrl } = await createCheckoutSession({
                credits: selectedPlan.credits,
                product: selectedPlan.stripeProductId,
                vinCode,
            });
            window.location.href = sessionUrl;
        } catch {
            globalNotification.open({
                type: notificationType.ERROR,
                message: getErrorTranslations(lang, 'paymentError', 'message'),
                description: getErrorTranslations(lang, 'paymentError', 'description'),
                placement: 'top',
            });
        }
    };

    return (
        <PaymentContext.Provider value={{ plans, selectedPlan, setSelectedPlan, initiatePayment }}>
            {children}
        </PaymentContext.Provider>
    );
};

export const usePaymentContext = () => useContext(PaymentContext);
